import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';

import { useFetchQuestionnaireFields } from '../../../../services/claims';
import { EYEBROW, SIDE_CARD } from './SideCards';

// The client's questionnaire answers (Nic 2026-10-10: keep it calm and easy
// to scan). One card: each area that affects them, with only what applies in
// plain words, then the areas that don't, as one short line. Read only, the
// same answers staff see.

// "Are you experiencing any Cardio symptoms?" -> "Cardio".
const areaOf = (question) =>
  String(question || '')
    .replace(/^are you experiencing any\s+/i, '')
    .replace(/\s+(related\s+)?symptoms\??$/i, '')
    .replace(/\?$/, '')
    .trim() || question;

const isYes = (v) => String(v || '').toLowerCase() === 'yes';
const isNo = (v) => String(v || '').toLowerCase() === 'no';

const ClientAnswers = ({ claimId, answers }) => {
  const { isLoading, data: questionnaire } = useFetchQuestionnaireFields();

  if (!answers) {
    return (
      <section className={SIDE_CARD}>
        <div className="py-8 text-center">
          <div className="text-base font-bold text-slate-900 dark:text-white">You haven&apos;t finished your questionnaire</div>
          <p className="mx-auto mb-4 mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">
            It takes a few minutes and tells us where to focus your claim.
          </p>
          <Link
            to={`/questionnaire?claimId=${claimId}`}
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white no-underline hover:bg-slate-700"
          >
            Complete your questionnaire <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    );
  }
  if (isLoading || !questionnaire) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 size={22} className="animate-spin text-slate-400" />
      </div>
    );
  }

  const rows = questionnaire.map((q) => {
    const a = answers[q.id] || { value: null, selections: {} };
    return {
      id: q.id,
      area: areaOf(q.question),
      value: a.value,
      applies: (q.subQuestions || [])
        .filter((s) => isYes((a.selections || {})[s.id]))
        .map((s) => s.question),
    };
  });
  const yes = rows.filter((r) => isYes(r.value));
  const no = rows.filter((r) => isNo(r.value));
  const open = rows.filter((r) => !isYes(r.value) && !isNo(r.value));

  return (
    <section className={SIDE_CARD}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="mb-0 text-base font-bold text-slate-900 dark:text-white">What you told us</h3>
        <span className="text-xs text-slate-500 dark:text-slate-400">Something changed? Let your team know.</span>
      </div>

      {yes.length ? (
        <>
          <div className={`${EYEBROW} mt-5`}>Affecting you</div>
          <ul className="mb-0 mt-1 list-none divide-y divide-slate-100 pl-0 dark:divide-white/[0.06]">
            {yes.map((r) => (
              <li key={r.id} className="grid gap-x-6 gap-y-0.5 py-3 sm:grid-cols-[220px_minmax(0,1fr)]">
                <div className="text-sm font-semibold text-slate-900 dark:text-white">{r.area}</div>
                <div className="text-sm text-slate-600 dark:text-slate-300">
                  {r.applies.length ? r.applies.join(', ') : 'Yes'}
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {no.length ? (
        <>
          <div className={`${EYEBROW} mt-5`}>Not affecting you</div>
          <p className="mb-0 mt-2 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
            {no.map((r) => r.area).join(', ')}
          </p>
        </>
      ) : null}

      {open.length ? (
        <>
          <div className={`${EYEBROW} mt-5`}>Not answered</div>
          <p className="mb-0 mt-2 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
            {open.map((r) => r.area).join(', ')}
          </p>
        </>
      ) : null}
    </section>
  );
};

export default ClientAnswers;
